/**
 * Fond anime "grand fond" : vagues de lumiere et bulles qui montent, dessinees sur un canvas 2D.
 *
 * 1.59.0.0 (demande du club : "peux-tu ajouter aux themes React Bits ?", puis "un quatrieme theme
 * et l'ecran de connexion") : inspire des fonds animes de React Bits (Waves, Aurora), reecrit sans
 * dependance - les originaux s'appuient sur WebGL (ogl, three) et gsap, trop lourds pour des
 * benevoles sur des machines modestes, et l'image de l'interface est construite sans fichier de
 * verrouillage des dependances.
 *
 * Sobriete voulue :
 *  - JAMAIS sur telephone ni ecran tactile (decision du club) ni quand l'appareil demande de
 *    reduire les animations : un degrade fixe a la place ;
 *  - 30 images/s au plus, resolution 1x, pause quand l'onglet est cache ;
 *  - aucun evenement souris capte (pointer-events: none), purement decoratif (aria-hidden).
 */
import React, { useEffect, useRef, useState } from 'react';

const REQUETE_SANS_ANIMATION = '(max-width: 767px), (pointer: coarse), (prefers-reduced-motion: reduce)';

const sansAnimation = () => {
  try { return window.matchMedia(REQUETE_SANS_ANIMATION).matches; } catch { return true; }
};

const lireCouleur = (nom, repli) => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(nom).trim();
  return v ? `hsl(${v.split(' ').join(', ')}` : repli;
};

const FondAnime = ({ className = '', style }) => {
  const canvasRef = useRef(null);
  const [statique, setStatique] = useState(sansAnimation);

  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(REQUETE_SANS_ANIMATION); } catch { return undefined; }
    const maj = () => setStatique(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', maj) : mq.addListener(maj);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', maj) : mq.removeListener(maj));
  }, []);

  useEffect(() => {
    if (statique) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let largeur = 0, hauteur = 0, raf = 0, dernier = 0, t = 0;
    const bulles = [];

    const dimensionner = () => {
      const r = canvas.getBoundingClientRect();
      largeur = Math.max(1, Math.round(r.width));
      hauteur = Math.max(1, Math.round(r.height));
      canvas.width = largeur;
      canvas.height = hauteur;
      bulles.length = 0;
      const n = Math.round((largeur * hauteur) / 38000);
      for (let i = 0; i < n; i++) {
        bulles.push({ x: Math.random() * largeur, y: Math.random() * hauteur, r: 1 + Math.random() * 3.2, v: 0.25 + Math.random() * 0.7, p: Math.random() * Math.PI * 2 });
      }
    };

    const dessiner = (maintenant) => {
      raf = requestAnimationFrame(dessiner);
      if (document.hidden || maintenant - dernier < 33) return;
      dernier = maintenant;
      t += 0.006;
      const primaire = lireCouleur('--primary', 'hsl(190, 70%, 55%');
      // 1.59.0.1 : sur fond sombre, des vagues a 3-7 % d'opacite ne se voyaient plus.
      const f = document.documentElement.classList.contains('dark') ? 2.4 : 1.4;
      ctx.clearRect(0, 0, largeur, hauteur);
      // Vagues de lumiere : 4 bandes sinusoidales, lentes, tres transparentes.
      for (let k = 0; k < 4; k++) {
        ctx.beginPath();
        const base = hauteur * (0.28 + k * 0.17);
        const amplitude = 18 + k * 9;
        for (let x = 0; x <= largeur; x += 12) {
          const y = base + Math.sin(x * 0.0042 + t * (1.2 + k * 0.35) + k) * amplitude
                         + Math.sin(x * 0.0017 - t * 0.8 + k * 2) * amplitude * 0.6;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.lineTo(largeur, hauteur);
        ctx.lineTo(0, hauteur);
        ctx.closePath();
        ctx.fillStyle = `${primaire}, ${(0.035 + k * 0.012) * f})`;
        ctx.fill();
      }
      // Bulles qui montent en ondulant.
      ctx.fillStyle = `${primaire}, ${Math.min(0.5, 0.22 * f)})`;
      for (const b of bulles) {
        b.y -= b.v;
        b.p += 0.02;
        if (b.y < -10) { b.y = hauteur + 10; b.x = Math.random() * largeur; }
        ctx.beginPath();
        ctx.arc(b.x + Math.sin(b.p) * 6, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    dimensionner();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(dimensionner) : null;
    ro ? ro.observe(canvas) : window.addEventListener('resize', dimensionner);
    raf = requestAnimationFrame(dessiner);
    return () => {
      cancelAnimationFrame(raf);
      ro ? ro.disconnect() : window.removeEventListener('resize', dimensionner);
    };
  }, [statique]);

  if (statique) {
    return <div aria-hidden="true" className={`fond-anime fond-anime-statique ${className}`} style={style} data-testid="fond-anime-statique" />;
  }
  return <canvas ref={canvasRef} aria-hidden="true" className={`fond-anime ${className}`} style={style} data-testid="fond-anime" />;
};

export default FondAnime;
